import type {
  MouseEvent,
  MouseEventHandler,
  TouchEvent,
  TouchEventHandler
} from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  createReadableClass,
  getColorWithAlpha,
  isTransparentColor,
  useViewportWidth
} from '@elfsight/widget-utils';
import type { DefaultComponentProps } from '../../types';
import { useSocialFeedSettings } from '../../utils';
import { useTabs } from '../TabsProvider';
import { ARROW_WIDTH, TabsSliderArrow } from './TabsSliderArrow';

type ShowArrowsState = Record<'left' | 'right', boolean>;

type TabsSliderProps = DefaultComponentProps & {
  // eslint-disable-next-line no-undef
  children: JSX.Element[];
};

export function TabsSlider({ children: tabs, className }: TabsSliderProps) {
  const {
    widgetWidth,
    tabsGrouping,
    tabsBackgroundColor,
    tabsTextColor: sliderArrowColor
  } = useSocialFeedSettings();
  const {
    currentTab: { id: currentTabId }
  } = useTabs();
  const [showArrows, setShowArrows] = useState<ShowArrowsState>({
    left: false,
    right: false
  });

  const viewportWidth = useViewportWidth(true);
  const innerRef = useRef<HTMLDivElement>(null);
  const itemActiveRef = useRef<HTMLDivElement>(null);

  const isDragging = useRef(false);
  const startX = useRef(0);

  const hasBackgroundColor = !isTransparentColor(tabsBackgroundColor);

  // Скролл к текущему активному табу
  useEffect(() => {
    if (!itemActiveRef.current || !innerRef.current) {
      return;
    }

    const active = itemActiveRef.current;
    const container = innerRef.current;
    const activeRect = active.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const isActiveTabOutOfView =
      activeRect.left < containerRect.left + ARROW_WIDTH ||
      activeRect.right > containerRect.right - ARROW_WIDTH;

    if (isActiveTabOutOfView) {
      isDragging.current = false;
      const scrollLeft =
        active.offsetLeft - container.offsetWidth / 2 + active.offsetWidth / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }, [tabs, viewportWidth, widgetWidth, tabsGrouping]);

  const checkArrowVisible = useCallback(() => {
    if (innerRef.current) {
      setShowArrows({
        left: innerRef.current.scrollLeft - ARROW_WIDTH > 0,
        right:
          innerRef.current.scrollWidth -
            Math.ceil(innerRef.current.scrollLeft) -
            ARROW_WIDTH -
            1 >
          innerRef.current.offsetWidth
      });
    }
  }, [innerRef]);

  const scrollInner = useCallback(
    (offset: number) => {
      if (innerRef.current) {
        innerRef.current.scrollLeft += offset;
      }
    },
    [innerRef]
  );

  const startScrolling = useCallback(
    (e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
      isDragging.current = true;
      startX.current =
        e.type === 'touchstart'
          ? (e as TouchEvent).touches[0].pageX
          : (e as MouseEvent).pageX;
    },
    []
  );

  const endScrolling = useCallback(() => {
    requestAnimationFrame(() => {
      isDragging.current = false;
    });
  }, []);

  const scrollWithDrag = useCallback(
    (e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
      if (!isDragging.current) return;

      const x =
        e.type === 'touchmove'
          ? (e as TouchEvent).touches[0].pageX
          : (e as MouseEvent).pageX;
      const walk = -(x - startX.current) * 2;

      scrollInner(walk);
    },
    [scrollInner]
  );

  useEffect(() => {
    if (!innerRef.current) return;

    checkArrowVisible();
  }, [
    checkArrowVisible,
    innerRef,
    viewportWidth,
    widgetWidth,
    tabsGrouping,
    tabs.length
  ]);

  if (!tabs?.length) {
    return null;
  }

  return (
    <Container
      className={className}
      _sliderArrowColor={sliderArrowColor}
      _showArrows={showArrows}
      _hasBackgroundColor={hasBackgroundColor}
    >
      <Inner
        ref={innerRef}
        onScroll={checkArrowVisible as MouseEventHandler<HTMLDivElement>}
        onMouseDown={startScrolling as MouseEventHandler<HTMLDivElement>}
        onMouseMove={scrollWithDrag as MouseEventHandler<HTMLDivElement>}
        onMouseUp={endScrolling as MouseEventHandler<HTMLDivElement>}
        onMouseLeave={endScrolling as MouseEventHandler<HTMLDivElement>}
        onTouchStart={startScrolling as TouchEventHandler<HTMLDivElement>}
        onTouchMove={scrollWithDrag as TouchEventHandler<HTMLDivElement>}
        onTouchEnd={endScrolling as TouchEventHandler<HTMLDivElement>}
        onTouchCancel={endScrolling as TouchEventHandler<HTMLDivElement>}
      >
        {tabs.map((item) => {
          const isActive = item.props.id === currentTabId;

          return (
            <ColumnsSliderItem
              key={item.key}
              ref={isActive ? itemActiveRef : null}
              _isActive={isActive}
            >
              {item}
            </ColumnsSliderItem>
          );
        })}
      </Inner>

      <Navigation>
        <TabsSliderArrow
          direction="left"
          onClick={() => scrollInner(-90)}
          visible={showArrows.left}
        />

        <TabsSliderArrow
          direction="right"
          onClick={() => scrollInner(90)}
          visible={showArrows.right}
        />
      </Navigation>
    </Container>
  );
}

export const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'tabs-slider',
    element: 'container'
  })
})<{
  _sliderArrowColor: string;
  _showArrows: ShowArrowsState;
  _hasBackgroundColor: boolean;
}>`
  position: relative;
  display: inline-flex;
  max-width: 100%;
  overflow: hidden;
  margin: ${({ _hasBackgroundColor }) => (_hasBackgroundColor ? '0 4px' : '0')};

  ${({ _showArrows }) => {
    if (_showArrows.left && _showArrows.right) {
      return `padding: 0 ${ARROW_WIDTH}px;`;
    }
    if (_showArrows.left) {
      return `padding-left: ${ARROW_WIDTH}px;`;
    }
    if (_showArrows.right) {
      return `padding-right: ${ARROW_WIDTH}px;`;
    }
    return;
  }};

  & * {
    user-select: none;
  }

  &::after {
    content: '';
    position: absolute;
    display: block;
    inset: auto 12px 0;
    height: 1px;
    background-color: ${({ _sliderArrowColor }) =>
      getColorWithAlpha(_sliderArrowColor, 0.1)};
  }
`;

const Inner = styled.div.attrs({
  className: createReadableClass({
    component: 'tabs-slider',
    element: 'inner'
  })
})`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  padding: 0 12px;
  height: 100%;
  gap: 24px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-snap-type: x;
  scroll-behavior: smooth;
  margin-bottom: -1px;
  border-bottom: 1px solid transparent;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

const Navigation = styled.div.attrs({
  className: createReadableClass({
    component: 'tabs-slider',
    element: 'navigation'
  })
})``;

const ColumnsSliderItem = styled.div.attrs<{
  _isActive: boolean;
}>(({ _isActive }) => ({
  className: createReadableClass({
    component: 'tabs-slider',
    element: 'item',
    index: _isActive && 'active'
  })
}))``;
